import React, { useState } from "react";
import {
  fetchMusicLinks,
  MusicLinksResult,
  MusicfetchMode,
} from "../../services/AdminService";

interface MusicLinkFetcherProps {
  onLinksFound: (result: MusicLinksResult | null) => void;
}

export const MusicLinkFetcher = ({ onLinksFound }: MusicLinkFetcherProps) => {
  const [mfMode, setMfMode] = useState<MusicfetchMode>("url");
  const [country, setCountry] = useState<string>("");
  const [mfInput, setMfInput] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [localResult, setLocalResult] = useState<MusicLinksResult | null>(null);

  const handleFetchLinks = async () => {
    setError("");
    setLocalResult(null);
    onLinksFound(null);

    const v = mfInput.trim();
    if (!v) {
      setError(`Please enter a ${mfMode.toUpperCase()}.`);
      return;
    }

    try {
      setLoading(true);
      const args =
        mfMode === "url"
          ? {
              mode: "url" as const,
              url: v,
              country: country.trim() || undefined,
            }
          : mfMode === "isrc"
            ? {
                mode: "isrc" as const,
                isrc: v,
                country: country.trim() || undefined,
              }
            : {
                mode: "upc" as const,
                upc: v,
                country: country.trim() || undefined,
              };

      const result = await fetchMusicLinks(args);
      setLocalResult(result);
      onLinksFound(result);
    } catch (e: any) {
      console.error(e);
      setError(e?.message || "Failed to fetch links.");
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setMfInput("");
    setCountry("");
    setLocalResult(null);
    setError("");
    onLinksFound(null);
  };

  return (
    <div className="admin-section">
      <h3 className="section-header">Fetch Streaming Links + Cover Art</h3>

      <div className="fetch-controls">
        <select
          className="admin-select"
          value={mfMode}
          onChange={(e) => setMfMode(e.target.value as MusicfetchMode)}
        >
          <option value="url">URL</option>
          <option value="isrc">ISRC</option>
          <option value="upc">UPC</option>
        </select>

        <input
          type="text"
          className="admin-input flex-grow"
          placeholder="Link / ISRC / UPC"
          value={mfInput}
          onChange={(e) => setMfInput(e.target.value)}
        />

        <input
          type="text"
          className="admin-input input-short"
          placeholder="Country"
          value={country}
          onChange={(e) => setCountry(e.target.value.toUpperCase())}
        />

        <button
          className="btn-primary"
          onClick={handleFetchLinks}
          disabled={loading}
        >
          {loading ? "Fetching..." : "Get Links"}
        </button>

        <button className="btn-secondary" onClick={handleClear}>
          Clear
        </button>
      </div>
      {error && <p className="error-text mt-2">{error}</p>}

      {localResult && (
        <div className="results-container">
          <div className="results-header">
            {localResult.coverArtUrl ? (
              <a
                href={localResult.coverArtUrl}
                target="_blank"
                rel="noreferrer"
              >
                <img
                  src={localResult.coverArtUrl}
                  alt="Art"
                  className="cover-art"
                />
              </a>
            ) : (
              <div className="cover-art placeholder" />
            )}
            <div className="meta-info">
              <div className="meta-title">{localResult.name || "Unknown"}</div>
              <div className="meta-sub">{localResult.type}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
